import { buildMatrix, renderStyledSvg } from "./styledQr/renderSvg";
import { composeArtwork } from "./export/composeArtwork";
import { composeFrameSVG } from "./export/composeFramesSvg";
import { extractInner, tightSVG } from "./export/svgTight";
import { EXPORT_PX } from "./export/exportConfig";

export type RenderQrArtworkArgs = {
  data: string;
  ecc: "L" | "M" | "Q" | "H";
  colors: {
    background: string;
    modules: string;
    eyeOuter: string;
    eyeInner: string;
  };
  shapes: {
    finder?: string; // outer eye
    center?: string; // inner eye
    data?: string;   // modules
  };
  roundness?: number;
  logo?: { svg?: string; pngDataUrl?: string; sizePct?: number; backer?: boolean } | null;
  frame: "none" | "frame_bottom_label" | "frame_speech_above" | "frame_speech_below" | "frame_arrow_scanme";
  frameColor?: string;
  labelText?: string;
  userMargin: number; // modules units when unframed
};

export type RenderQrArtworkOut = {
  svg: string;
  width: number;
  height: number;
};

function mapUiFrameToComposerKey(frame: RenderQrArtworkArgs["frame"]): "none" | "basic" | "tabTop" | "tabBottom" | "arrow" {
  switch (frame) {
    case "none":
      return "none";
    case "frame_speech_above":
      return "tabTop";
    case "frame_speech_below":
      return "tabBottom";
    case "frame_arrow_scanme":
      return "arrow";
    case "frame_bottom_label":
    default:
      return "basic";
  }
}

function ensureSvgNamespaces(svgEl: SVGSVGElement) {
  if (!svgEl.getAttribute("xmlns")) svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  if (!svgEl.getAttribute("xmlns:xlink")) svgEl.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  svgEl.setAttribute("version", "1.1");
}

function injectLogoIntoSvg(svgString: string, logo: { svg?: string; pngDataUrl?: string; sizePct: number; backer?: boolean }): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svgEl = doc.documentElement as unknown as SVGSVGElement;
  const ns = svgEl.namespaceURI || "http://www.w3.org/2000/svg";
  ensureSvgNamespaces(svgEl);

  const vb = (svgEl.getAttribute("viewBox") || "").split(/\s+/).map(Number);
  let size = 256;
  if (vb.length === 4 && !Number.isNaN(vb[2])) size = vb[2];
  else {
    const w = parseFloat(svgEl.getAttribute("width") || "256");
    const h = parseFloat(svgEl.getAttribute("height") || "256");
    size = Math.max(w, h) || 256;
  }

  const pct = Math.max(10, Math.min(logo.sizePct, 40));
  const logoSize = Math.round(size * (pct / 100));
  const pos = Math.round((size - logoSize) / 2);
  const inset = logo.backer ? Math.max(1, Math.round(logoSize * 0.02)) : 0; // subtle white margin
  const contentSize = Math.max(1, logoSize - inset * 2);
  const g = doc.createElementNS(ns, "g");
  g.setAttribute("id", "logo-layer");
  g.setAttribute("style", "pointer-events:none");

  // Optional white backer behind the logo for better contrast
  if (logo.backer) {
    const rect = doc.createElementNS(ns, "rect");
    rect.setAttribute("x", String(pos));
    rect.setAttribute("y", String(pos));
    rect.setAttribute("width", String(logoSize));
    rect.setAttribute("height", String(logoSize));
    const r = Math.round(logoSize * 0.18);
    rect.setAttribute("rx", String(r));
    rect.setAttribute("ry", String(r));
    rect.setAttribute("fill", "#ffffff");
    g.appendChild(rect);
  }

  if (logo.svg) {
    // Inline the provided SVG markup scaled to fit
    const innerRoot = new DOMParser().parseFromString(logo.svg, "image/svg+xml").documentElement as unknown as SVGSVGElement;
    let innerSize = 256;
    const ivb = (innerRoot.getAttribute("viewBox") || "").split(/\s+/).map(Number);
    if (ivb.length === 4 && !Number.isNaN(ivb[2])) innerSize = ivb[2];
    else {
      const iw = parseFloat(innerRoot.getAttribute("width") || "256");
      const ih = parseFloat(innerRoot.getAttribute("height") || "256");
      innerSize = Math.max(iw, ih) || 256;
    }
    const scale = contentSize / innerSize;
    const wrap = doc.createElementNS(ns, "g");
    wrap.setAttribute("transform", `translate(${pos + inset},${pos + inset}) scale(${scale})`);
    while (innerRoot.firstChild) {
      wrap.appendChild(doc.importNode(innerRoot.firstChild, true));
      innerRoot.removeChild(innerRoot.firstChild);
    }
    g.appendChild(wrap);
  } else if (logo.pngDataUrl) {
    const img = doc.createElementNS(ns, "image");
    // Support both href forms for broader compatibility
    img.setAttributeNS("http://www.w3.org/1999/xlink", "href", logo.pngDataUrl);
    img.setAttribute("href", logo.pngDataUrl);
    img.setAttribute("x", String(pos + inset));
    img.setAttribute("y", String(pos + inset));
    img.setAttribute("width", String(contentSize));
    img.setAttribute("height", String(contentSize));
    img.setAttribute("preserveAspectRatio", "xMidYMid meet");
    g.appendChild(img);
  }

  svgEl.appendChild(g);
  return new XMLSerializer().serializeToString(svgEl);
}

export async function renderQrArtwork(args: RenderQrArtworkArgs): Promise<RenderQrArtworkOut> {
  const { data, ecc } = args;
  const frameKey = mapUiFrameToComposerKey(args.frame);

  // Generate base QR SVG with margin policy
  const mx = buildMatrix(data, ecc);
  const marginModules = frameKey === "none" ? Math.max(0, Math.floor(args.userMargin)) : 0;
  const baseSvg = renderStyledSvg(mx, {
    ecc,
    margin: marginModules,
    moduleShape: (args.shapes?.data as any) || "square",
    roundness: args.roundness ?? 0,
    colors: {
      background: args.colors.background,
      modules: args.colors.modules,
      eyeOuter: args.colors.eyeOuter,
      eyeInner: args.colors.eyeInner,
    },
    eyeOuterShape: (args.shapes?.finder as any) || "square",
    eyeInnerShape: (args.shapes?.center as any) || "square",
  } as any);

  // Optional logo
  let withLogo = baseSvg;
  if (args.logo && (args.logo.svg || args.logo.pngDataUrl)) {
    withLogo = injectLogoIntoSvg(baseSvg, {
      svg: args.logo.svg,
      pngDataUrl: args.logo.pngDataUrl,
      sizePct: Math.max(10, Math.min(args.logo.sizePct ?? 18, 40)),
      backer: !!args.logo.backer,
    });
  }

  // Compose frame or square and tighten
  if (frameKey !== "none") {
    const base = extractInner(withLogo);
    const c = composeFrameSVG({
      frame: frameKey === "tabTop" ? "tabTop" : frameKey === "tabBottom" ? "tabBottom" : frameKey === "arrow" ? "arrow" : "basic",
      qrInner: base.inner,
      qrViewBox: { w: base.w, h: base.h },
      frameColor: args.frameColor ?? "#000",
      textColor: "#fff",
      label: args.labelText ?? "SCAN ME",
      bgColor: args.colors.background,
    });
    const tight = tightSVG(c.inner, c.nativeSize);
    return { svg: tight, width: c.nativeSize.w, height: c.nativeSize.h };
  } else {
    // Unframed: use tight viewBox based on QR module units (size + 2*quietZone)
    const ex = extractInner(withLogo);
    const nat = EXPORT_PX.square;
    const svg = `\n<svg\n  xmlns="http://www.w3.org/2000/svg"\n  xmlns:xlink="http://www.w3.org/1999/xlink"\n  width="${nat.w}px"\n  height="${nat.h}px"\n  viewBox="0 0 ${ex.w} ${ex.h}"\n  preserveAspectRatio="xMidYMid meet"\n>\n  <g id="qr">\n    ${ex.inner}\n  </g>\n</svg>`.trim();
    return { svg, width: nat.w, height: nat.h };
  }
}


